import React, { useState } from 'react';
import { Table, Button, Space, Tag, Typography, Card, Modal, Popconfirm, message, Descriptions, Divider } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, EyeOutlined, ThunderboltOutlined, HistoryOutlined, CopyOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;

export default function VersionHistoryTab({ configurations, selectedType: _selectedType }) {
  const { t } = useTranslation(['tables']);
  const queryClient = useQueryClient();
  const [compareModalVisible, setCompareModalVisible] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);

  const pa = (key) => t(`payrollAutomation.agentConfig.${key}`, { ns: 'tables', defaultValue: key });

  // Activate mutation
  const activateMutation = useMutation({
    mutationFn: (id) => API.agentConfig.activateConfiguration(id),
    onSuccess: () => {
      message.success(pa('configActivated'));
      queryClient.invalidateQueries(['agent-configs']);
      queryClient.invalidateQueries(['active-agent-config']);
      queryClient.invalidateQueries(['agent-config-detail']);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || pa('activationFailed'));
    },
  });

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: (id) => API.agentConfig.deactivateConfiguration(id),
    onSuccess: () => {
      message.success(pa('configDeactivated') || 'Configuration deactivated successfully');
      queryClient.invalidateQueries(['agent-configs']);
      queryClient.invalidateQueries(['active-agent-config']);
      queryClient.invalidateQueries(['agent-config-detail']);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || pa('deactivationFailed') || 'Failed to deactivate configuration');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => API.agentConfig.deleteAgentConfiguration(id),
    onSuccess: () => {
      message.success(pa('configDeleted'));
      queryClient.invalidateQueries(['agent-configs']);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || pa('deleteFailed'));
    },
  });

  // Clone mutation
  const cloneMutation = useMutation({
    mutationFn: (id) => API.agentConfig.cloneConfiguration(id),
    onSuccess: (data) => {
      message.success(pa('configCloned') || `Configuration cloned successfully as ${data.data.name}`);
      queryClient.invalidateQueries(['agent-configs']);
      queryClient.invalidateQueries(['agent-config-detail']);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || pa('cloneFailed') || 'Failed to clone configuration');
    },
  });

  const handleActivate = (id) => {
    activateMutation.mutate(id);
  };

  const handleDeactivate = (id) => {
    deactivateMutation.mutate(id);
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const handleClone = (id) => {
    cloneMutation.mutate(id);
  };

  const handleViewDetails = (config) => {
    setSelectedConfig(config);
    setCompareModalVisible(true);
  };

  const columns = [
    {
      title: pa('version'),
      dataIndex: 'version',
      key: 'version',
      width: 80,
      render: (version) => <Tag color="blue">v{version}</Tag>,
    },
    {
      title: pa('name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: pa('status'),
      dataIndex: 'active',
      key: 'status',
      width: 100,
      render: (active, record) => {
        if (active) {
          return <Tag color="green" icon={<CheckCircleOutlined />}>{pa('active')}</Tag>;
        }
        return <Tag color="default">{record.status ? pa(record.status) : pa('archived')}</Tag>;
      },
    },
    {
      title: pa('model'),
      dataIndex: 'modelVersion',
      key: 'modelVersion',
      width: 180,
      render: (model) => {
        const modelName = model?.replace('claude-', '')?.replace('20241022', '(Latest)') || 'N/A';
        return <Text style={{ fontSize: 12 }}>{modelName}</Text>;
      },
    },
    {
      title: pa('avgConfidence'),
      dataIndex: ['metrics', 'avgConfidence'],
      key: 'avgConfidence',
      width: 120,
      render: (avgConfidence) => {
        const confidence = avgConfidence || 0;
        const color = confidence >= 80 ? 'green' : confidence >= 60 ? 'orange' : 'red';
        return <Tag color={color}>{confidence.toFixed(1)}%</Tag>;
      },
    },
    {
      title: pa('totalParses'),
      dataIndex: ['metrics', 'totalParses'],
      key: 'totalParses',
      width: 100,
      render: (total) => total || 0,
    },
    {
      title: pa('totalCost'),
      dataIndex: ['metrics', 'totalCost'],
      key: 'totalCost',
      width: 100,
      render: (cost) => `$${(cost || 0).toFixed(2)}`,
    },
    {
      title: pa('createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: pa('actions'),
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            {pa('view')}
          </Button>

          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleClone(record._id)}
            loading={cloneMutation.isPending}
          >
            {pa('clone') || 'Clone'}
          </Button>

          {!record.active ? (
            <>
              <Button
                size="small"
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={() => handleActivate(record._id)}
                loading={activateMutation.isPending}
              >
                {pa('activate')}
              </Button>

              <Popconfirm
                title={pa('deleteConfirmTitle')}
                description={pa('deleteConfirmDesc')}
                onConfirm={() => handleDelete(record._id)}
                okText={pa('yes')}
                cancelText={pa('no')}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteMutation.isPending}
                />
              </Popconfirm>
            </>
          ) : (
            <Popconfirm
              title={pa('deactivateConfirmTitle') || 'Deactivate this configuration?'}
              description={pa('deactivateConfirmDesc') || 'This will stop using this configuration for new submissions.'}
              onConfirm={() => handleDeactivate(record._id)}
              okText={pa('yes')}
              cancelText={pa('no')}
            >
              <Button
                size="small"
                danger
                icon={<CloseCircleOutlined />}
                loading={deactivateMutation.isPending}
              >
                {pa('deactivate') || 'Deactivate'}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card title={<><HistoryOutlined /> {pa('versionHistory')}</>}>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Text strong>{pa('totalVersions')}:</Text>
            <Tag color="blue">{configurations.length}</Tag>
            <Text strong>{pa('activeVersion')}:</Text>
            <Tag color="green">
              {configurations.find((c) => c.active)?.version
                ? `v${configurations.find((c) => c.active).version}`
                : pa('none')}
            </Tag>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={configurations}
          rowKey={(record) => record._id}
          pagination={{ pageSize: 10 }}
          size="small"
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Configuration Details Modal */}
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            {selectedConfig?.name} - v{selectedConfig?.version}
            {selectedConfig?.active && <Tag color="green">{pa('active')}</Tag>}
          </Space>
        }
        open={compareModalVisible}
        onCancel={() => setCompareModalVisible(false)}
        footer={null}
        width={900}
      >
        {selectedConfig && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label={pa('type')}>{selectedConfig.type}</Descriptions.Item>
              <Descriptions.Item label={pa('status')}>
                {selectedConfig.active ? (
                  <Tag color="green">{pa('active')}</Tag>
                ) : (
                  <Tag>{selectedConfig.status}</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={pa('modelVersion')}>{selectedConfig.modelVersion}</Descriptions.Item>
              <Descriptions.Item label={pa('temperature')}>{selectedConfig.temperature}</Descriptions.Item>
              <Descriptions.Item label={pa('maxTokens')}>{selectedConfig.maxTokens}</Descriptions.Item>
              <Descriptions.Item label={pa('timeout')}>{selectedConfig.timeout / 1000}s</Descriptions.Item>
              <Descriptions.Item label={pa('createdAt')}>
                {dayjs(selectedConfig.createdAt).format('DD.MM.YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label={pa('updatedAt')}>
                {dayjs(selectedConfig.updatedAt).format('DD.MM.YYYY HH:mm')}
              </Descriptions.Item>
            </Descriptions>

            <Divider>{pa('systemPrompt')}</Divider>
            <Card size="small" style={{ backgroundColor: '#f5f5f5' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12, fontFamily: 'monospace' }}>
                {selectedConfig.systemPrompt}
              </pre>
            </Card>

            {selectedConfig.description && (
              <>
                <Divider>{pa('description')}</Divider>
                <Paragraph>{selectedConfig.description}</Paragraph>
              </>
            )}

            {selectedConfig.metrics && (
              <>
                <Divider>{pa('metrics')}</Divider>
                <Descriptions bordered column={3} size="small">
                  <Descriptions.Item label={pa('totalParses')}>
                    {selectedConfig.metrics.totalParses || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label={pa('successfulParses')}>
                    {selectedConfig.metrics.successfulParses || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label={pa('avgConfidence')}>
                    {(selectedConfig.metrics.avgConfidence || 0).toFixed(1)}%
                  </Descriptions.Item>
                  <Descriptions.Item label={pa('avgParsingTime')}>
                    {((selectedConfig.metrics.avgParsingTime || 0) / 1000).toFixed(1)}s
                  </Descriptions.Item>
                  <Descriptions.Item label={pa('totalCost')}>
                    ${(selectedConfig.metrics.totalCost || 0).toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label={pa('lastUsed')}>
                    {selectedConfig.metrics.lastUsed
                      ? dayjs(selectedConfig.metrics.lastUsed).format('DD.MM.YYYY HH:mm')
                      : 'N/A'}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            {selectedConfig.changeLog && selectedConfig.changeLog.length > 0 && (
              <>
                <Divider>{pa('changeLog')}</Divider>
                <Card size="small">
                  {selectedConfig.changeLog.map((log, idx) => (
                    <div key={idx} style={{ marginBottom: 8 }}>
                      <Space>
                        <Tag color="blue">v{log.version}</Tag>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {dayjs(log.changedAt).format('DD.MM.YYYY HH:mm')}
                        </Text>
                      </Space>
                      <Paragraph style={{ marginLeft: 16, marginTop: 4, marginBottom: 0 }}>
                        {log.changes}
                      </Paragraph>
                    </div>
                  ))}
                </Card>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
